"use client";

import { useMemo, useState } from "react";
import { ArrowRight, Button, Check, CircleAlert, Icon, Input, cx } from "@/components";
import { isGoogleSheetUrl } from "@/lib/ingest/sheets";
import { summarizePaste, delimiterLabel } from "./pasteSummary";

/**
 * First-class "Link a Google Sheet" (FAIL-474) — the create-surface replacement
 * for the bare homepage URL box. It VALIDATES the link client-side, fetches the
 * sheet through the existing `/api/ingest/sheets` CSV proxy, then PREVIEWS the
 * rows (the same `summarizePaste` the paste panel uses) so you confirm real data
 * before building. A private/unshared sheet — the #1 failure — surfaces the one
 * precise, actionable message from the proxy, not a generic error. Submitting
 * hands the CSV + source URL up; the editor registers it as a live source on save.
 */
const PREVIEW_ROWS = 5;

type Phase =
  | { s: "idle" }
  | { s: "fetching" }
  | { s: "loaded"; csv: string }
  | { s: "error"; message: string };

export function SheetPanel({
  onSubmit,
  onFetchStarted,
  onFetchSucceeded,
  onFetchFailed,
}: {
  onSubmit: (args: { csv: string; url: string }) => void;
  onFetchStarted?: () => void;
  onFetchSucceeded?: (rowCount: number) => void;
  onFetchFailed?: (failureCode: string) => void;
}) {
  const [url, setUrl] = useState("");
  const [hasHeader, setHasHeader] = useState(true);
  const [phase, setPhase] = useState<Phase>({ s: "idle" });

  const valid = isGoogleSheetUrl(url);
  const summary = useMemo(
    () => (phase.s === "loaded" ? summarizePaste(phase.csv, { hasHeader, previewRows: PREVIEW_ROWS }) : null),
    [phase, hasHeader],
  );

  function onUrlChange(next: string) {
    setUrl(next);
    // A link edit invalidates any prior fetch — clear the stale preview/error.
    if (phase.s !== "idle") setPhase({ s: "idle" });
  }

  async function fetchSheet() {
    if (!valid || phase.s === "fetching") return;
    onFetchStarted?.();
    setPhase({ s: "fetching" });
    try {
      const res = await fetch("/api/ingest/sheets", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ url: url.trim() }),
      });
      const body = (await res.json().catch(() => null)) as { csv?: string; error?: string } | null;
      if (!res.ok || typeof body?.csv !== "string" || body.csv.trim() === "") {
        onFetchFailed?.(!res.ok ? `http_${res.status}` : "empty_response");
        setPhase({
          s: "error",
          message: body?.error ?? "Could not read that Google Sheet. Download it as CSV and drop it here.",
        });
        return;
      }
      const fetchedSummary = summarizePaste(body.csv, { hasHeader, previewRows: PREVIEW_ROWS });
      onFetchSucceeded?.(fetchedSummary.dataRowCount);
      setPhase({ s: "loaded", csv: body.csv });
    } catch {
      onFetchFailed?.("network_error");
      setPhase({ s: "error", message: "Could not reach that Google Sheet. Check the link and try again." });
    }
  }

  function create() {
    if (phase.s !== "loaded" || !summary || summary.dataRowCount < 1) return;
    // The import path always expects a header row; synthesize one when the sheet
    // has none, so nothing is misread as data. Join with the SNIFFED delimiter
    // (Google CSV is comma, but a semicolon/tab export must match the body).
    const payload = hasHeader ? phase.csv : `${summary.header.join(summary.delimiter)}\n${phase.csv}`;
    onSubmit({ csv: payload, url: url.trim() });
  }

  const hiddenRows = summary ? summary.dataRowCount - summary.preview.length : 0;

  return (
    <div className="pz-sheet">
      <div className="pz-sheet__row">
        <Input
          id="intake-sheet"
          className="pz-sheet__url"
          type="url"
          value={url}
          placeholder="Paste the sheet’s share link"
          aria-label="Public Google Sheets link"
          spellCheck={false}
          onChange={(e) => onUrlChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void fetchSheet();
            }
          }}
        />
        <Button
          variant="primary"
          loading={phase.s === "fetching"}
          disabled={!valid || phase.s === "fetching"}
          iconTrailing={<Icon icon={ArrowRight} size="sm" />}
          onClick={() => void fetchSheet()}
        >
          {phase.s === "loaded" ? "Re-fetch" : "Fetch the sheet"}
        </Button>
      </div>
      <p className="pz-sheet__access">Be sure your Google Sheet share settings are set to “anyone with the link”</p>

      {phase.s === "error" && (
        <p className="pz-sheet__error" role="alert">
          <Icon icon={CircleAlert} size="sm" />
          {phase.message}
        </p>
      )}

      {phase.s === "loaded" && summary && (
        <>
          <div className="pz-paste__status">
            <span className="pz-paste__count">
              <Icon icon={Check} size="sm" />
              {summary.dataRowCount.toLocaleString()} {summary.dataRowCount === 1 ? "row" : "rows"} ×{" "}
              {summary.columnCount} {summary.columnCount === 1 ? "column" : "columns"}
            </span>
            <span className="pz-paste__delim">{delimiterLabel(summary.delimiter)}</span>
            <label className="pz-paste__toggle">
              <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
              First row is headers
            </label>
          </div>

          <div className="pz-paste__preview-wrap">
            <table className="pz-paste__preview">
              <thead>
                <tr>
                  {summary.header.map((h, i) => (
                    <th key={i} scope="col">
                      {h.trim() === "" ? "—" : h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.preview.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td key={c}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hiddenRows > 0 ? (
            <p className="pz-paste__more">
              +{hiddenRows.toLocaleString()} more {hiddenRows === 1 ? "row" : "rows"}
            </p>
          ) : null}

          <p className={cx("pz-sheet__sync")}>Linked — this map will stay in sync with the sheet.</p>

          <div className="pz-paste__actions">
            <Button
              variant="primary"
              disabled={summary.dataRowCount < 1}
              iconTrailing={<Icon icon={ArrowRight} size="sm" />}
              onClick={create}
            >
              Create live map
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
